import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ManageSearchRoundedIcon from '@mui/icons-material/ManageSearchRounded';
import { edgeAgentsApi } from '../api/edgeAgents';
import { buildingsApi } from '../api/buildings';

const NEW_USPD = '__new__';
const PULSAR_TCP_BASE = 4000;
const PULSAR_LINE_COUNT = 8;

const surfaceSx = {
  borderRadius: 4,
  border: '1px solid var(--border-color)',
  background: 'var(--surface-bg)',
  boxShadow: 'none',
};

const COMMAND_LABELS = {
  ping: 'Связь',
  ports: 'Порты',
  snapshot: 'Состояние',
  scan: 'Прозвонить',
  scan_line: 'Опрос линии',
  poll_device: 'Опрос прибора',
  collect_readings: 'Сбор показаний',
  discover: 'Найти на шине',
  search_device: 'Поиск прибора',
  cancel: 'Отмена',
  reset: 'Сброс задач',
};

function formatPct(value) {
  if (value === null || value === undefined || value === '') return '—';
  return `${Number(value).toFixed(0)}%`;
}

function formatUptime(sec) {
  if (!sec) return '—';
  const days = Math.floor(sec / 86400);
  const hours = Math.floor((sec % 86400) / 3600);
  if (days > 0) return `${days} д ${hours} ч`;
  const mins = Math.floor((sec % 3600) / 60);
  if (hours > 0) return `${hours} ч ${mins} мин`;
  return `${mins} мин`;
}

function formatTs(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('ru-RU');
  } catch {
    return String(value);
  }
}

function osLabel(name) {
  if (!name) return '';
  if (name === 'windows') return 'Windows';
  if (name === 'linux') return 'Linux';
  if (name === 'darwin') return 'macOS';
  return name;
}

function apiError(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length) {
    return detail.map((item) => item?.msg || item?.detail || String(item)).join('; ');
  }
  return fallback;
}

async function copyText(value) {
  if (!value) return;
  try {
    await navigator.clipboard.writeText(value);
  } catch {
    window.prompt('Скопируйте текст', value);
  }
}

function statusChip(agent) {
  if (!agent) return null;
  if (agent.status === 'revoked') return <Chip size="small" label="Отключён" />;
  if (agent.online) return <Chip size="small" color="success" label="На связи" />;
  if (agent.status === 'pending') return <Chip size="small" color="warning" label="Не подключён" />;
  return <Chip size="small" label="Нет связи" />;
}

function commandLabel(type) {
  return COMMAND_LABELS[type] || type;
}

function formatDuration(totalSeconds) {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;
  if (hours > 0) return `${hours} ч ${minutes} мин`;
  if (minutes > 0) return `${minutes} мин ${rest} с`;
  return `${rest} с`;
}

function TimeDistance({ value, direction = 'until' }) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!value) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [value]);

  if (!value) return null;
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return null;
  const seconds = direction === 'since' ? (now - timestamp) / 1000 : (timestamp - now) / 1000;
  if (direction === 'until' && seconds <= 0) return <>при первой возможности</>;
  return <>{formatDuration(seconds)}</>;
}

function AgentActivity({ agent }) {
  const command = agent?.meta?.current_command;
  const collection = agent?.meta?.reading_collection || {};
  const pending = Array.isArray(agent?.pending_commands) ? agent.pending_commands.length : 0;
  return (
    <Paper variant="outlined" sx={{ p: 1.25, mb: 2, borderRadius: 2.5 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
        <Box>
          <Typography variant="caption" color="text.secondary">Текущая операция</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {!agent?.online
              ? 'Нет связи с агентом'
              : command
              ? `${command.automatic ? 'Автоматически: ' : ''}${commandLabel(command.type)}`
              : 'Агент ожидает команду'}
          </Typography>
          {agent?.online && command?.started_at ? (
            <Typography variant="caption" color="text.secondary">
              Выполняется <TimeDistance value={command.started_at} direction="since" />
            </Typography>
          ) : null}
        </Box>
        <Box sx={{ textAlign: { sm: 'right' } }}>
          <Typography variant="caption" color="text.secondary">Следующий автоматический сбор</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {!agent?.online
              ? 'После восстановления связи'
              : collection.enabled === false
              ? 'Выключен'
              : collection.running
                ? 'После завершения текущего прохода'
                : collection.next_run_at
                  ? <TimeDistance value={collection.next_run_at} />
                  : 'При первой возможности'}
          </Typography>
          {pending ? (
            <Typography variant="caption" color="text.secondary">В очереди: {pending}</Typography>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );
}

function profileLabel(value) {
  if (value === 'puls_stk_rs485') return 'ПУЛЬСАР СТК';
  if (value === 'without_profile') return 'Без профиля';
  return value || 'Не указан';
}

function collectionLinkMessage(value) {
  const message = String(value || '').trim();
  if (/i\/o timeout|timed out/i.test(message)) return 'Порт не отвечает: превышено время ожидания';
  if (/connection refused/i.test(message)) return 'УСПД отклонил подключение к порту';
  if (/no route to host|host is unreachable/i.test(message)) return 'УСПД недоступен по сети';
  return message || 'Нет связи с портом';
}

function collectionStatusColor(status) {
  if (status === 'ok') return 'success.main';
  if (status === 'partial' || status === 'without_readings') return 'warning.main';
  return 'error.main';
}

function ReadingCollectionPanel({ agent, busy, commandBusy, onSave, onRun }) {
  const state = agent?.meta?.reading_collection || {};
  const saved = agent?.meta?.reading_collection_config || state;
  const [enabled, setEnabled] = useState(saved?.enabled !== false);
  const [intervalSec, setIntervalSec] = useState(Number(saved?.interval_sec) || 900);
  const [gatewayParallelism, setGatewayParallelism] = useState(Number(saved?.gateway_parallelism) || 4);
  const [requestDelayMs, setRequestDelayMs] = useState(Number(saved?.request_delay_ms) || 40);
  const [responseTimeoutMs, setResponseTimeoutMs] = useState(Number(saved?.response_timeout_ms) || 900);
  const [retryCount, setRetryCount] = useState(Number(saved?.retry_count) || 3);
  const [retryDelayMs, setRetryDelayMs] = useState(Number(saved?.retry_delay_ms) || 120);
  const [retryJitterPercent, setRetryJitterPercent] = useState(Number(saved?.retry_jitter_percent) || 20);
  const [adaptiveTimeout, setAdaptiveTimeout] = useState(saved?.adaptive_timeout !== false);
  const [circuitFailures, setCircuitFailures] = useState(Number(saved?.circuit_failures) || 3);
  const [circuitCooldownSec, setCircuitCooldownSec] = useState(Number(saved?.circuit_cooldown_sec) || 120);
  const [settingsExpanded, setSettingsExpanded] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setEnabled(saved?.enabled !== false);
    setIntervalSec(Number(saved?.interval_sec) || 900);
    setGatewayParallelism(Number(saved?.gateway_parallelism) || 4);
    setRequestDelayMs(Number(saved?.request_delay_ms) || 40);
    setResponseTimeoutMs(Number(saved?.response_timeout_ms) || 900);
    setRetryCount(Number(saved?.retry_count) || 3);
    setRetryDelayMs(Number(saved?.retry_delay_ms) || 120);
    setRetryJitterPercent(Number(saved?.retry_jitter_percent) || 20);
    setAdaptiveTimeout(saved?.adaptive_timeout !== false);
    setCircuitFailures(Number(saved?.circuit_failures) || 3);
    setCircuitCooldownSec(Number(saved?.circuit_cooldown_sec) || 120);
  }, [agent?.id, saved?.enabled, saved?.interval_sec, saved?.gateway_parallelism, saved?.request_delay_ms, saved?.response_timeout_ms, saved?.retry_count, saved?.retry_delay_ms, saved?.retry_jitter_percent, saved?.adaptive_timeout, saved?.circuit_failures, saved?.circuit_cooldown_sec]);

  const total = Number(state?.devices_total) || 0;
  const readings = Number(state?.readings) || 0;
  const noReply = Number(state?.no_reply) || 0;
  const mismatch = Number(state?.mismatch) || 0;
  const endpoints = (Array.isArray(state?.endpoints) ? state.endpoints : [])
    .filter((item) => Number(item?.total) > 0);
  const profiles = Object.entries(state?.profile_counts || {});
  const distributionWarnings = Array.isArray(state?.distribution_warnings) ? state.distribution_warnings : [];
  const suspendedGateways = (Array.isArray(agent?.meta?.bus_health) ? agent.meta.bus_health : [])
    .filter((item) => item?.open_until && new Date(item.open_until).getTime() > Date.now());
  const hasReport = Boolean(state?.last_finished_at || total);
  const hasProblems = hasReport && (readings < total || noReply > 0 || mismatch > 0);

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2.5 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
      >
        <Box>
          <Typography variant="subtitle2">Автоматический сбор показаний</Typography>
          <Typography variant="caption" color="text.secondary">
            {state?.running
              ? 'Сейчас выполняется опрос приборов'
              : state?.enabled !== false && state?.next_run_at
                ? <>Следующий проход через <TimeDistance value={state.next_run_at} /></>
              : state?.last_finished_at
                ? `Последний проход: ${formatTs(state.last_finished_at)}`
                : 'Проход ещё не выполнялся'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <FormControlLabel
            control={<Switch size="small" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />}
            label={enabled ? 'Включён' : 'Выключен'}
            sx={{ mr: 0.5 }}
          />
          <FormControl size="small" sx={{ minWidth: 210 }} disabled={!enabled}>
            <InputLabel>Пауза между проходами</InputLabel>
            <Select
              label="Пауза между проходами"
              value={intervalSec}
              onChange={(event) => setIntervalSec(Number(event.target.value))}
            >
              <MenuItem value={300}>5 минут</MenuItem>
              <MenuItem value={900}>15 минут</MenuItem>
              <MenuItem value={1800}>30 минут</MenuItem>
              <MenuItem value={3600}>1 час</MenuItem>
              <MenuItem value={10800}>3 часа</MenuItem>
              <MenuItem value={21600}>6 часов</MenuItem>
            </Select>
          </FormControl>
          <Button
            size="small"
            variant="outlined"
            disabled={busy}
            onClick={() => onSave({
              enabled,
              interval_sec: intervalSec,
              gateway_parallelism: gatewayParallelism,
              request_delay_ms: requestDelayMs,
              response_timeout_ms: responseTimeoutMs,
              retry_count: retryCount,
              retry_delay_ms: retryDelayMs,
              retry_jitter_percent: retryJitterPercent,
              adaptive_timeout: adaptiveTimeout,
              circuit_failures: circuitFailures,
              circuit_cooldown_sec: circuitCooldownSec,
            })}
          >
            Сохранить
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={busy || Boolean(commandBusy) || !agent?.enrolled_at}
            onClick={onRun}
          >
            Собрать сейчас
          </Button>
        </Stack>
      </Stack>

      <Button
        size="small"
        onClick={() => setSettingsExpanded((value) => !value)}
        endIcon={<KeyboardArrowDownRoundedIcon sx={{ transform: settingsExpanded ? 'rotate(180deg)' : 'none' }} />}
        sx={{ mt: 0.5, px: 0 }}
      >
        Параметры опроса
      </Button>
      <Collapse in={settingsExpanded} unmountOnExit>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mt: 0.75 }}>
          <TextField
            size="small"
            select
            label="УСПД параллельно"
            value={gatewayParallelism}
            onChange={(event) => setGatewayParallelism(Number(event.target.value))}
            sx={{ minWidth: 150 }}
          >
            {[1, 2, 3, 4, 6, 8].map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
          </TextField>
          <TextField size="small" type="number" label="Пауза, мс" value={requestDelayMs} onChange={(event) => setRequestDelayMs(Number(event.target.value))} inputProps={{ min: 0, max: 2000 }} />
          <TextField size="small" type="number" label="Таймаут, мс" value={responseTimeoutMs} onChange={(event) => setResponseTimeoutMs(Number(event.target.value))} inputProps={{ min: 200, max: 5000 }} />
          <TextField size="small" type="number" label="Попыток" value={retryCount} onChange={(event) => setRetryCount(Number(event.target.value))} inputProps={{ min: 1, max: 5 }} />
          <TextField size="small" type="number" label="Между попытками, мс" value={retryDelayMs} onChange={(event) => setRetryDelayMs(Number(event.target.value))} inputProps={{ min: 0, max: 2000 }} />
          <TextField size="small" type="number" label="Разброс паузы, %" value={retryJitterPercent} onChange={(event) => setRetryJitterPercent(Number(event.target.value))} inputProps={{ min: 0, max: 50 }} />
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} sx={{ mt: 1 }}>
          <FormControlLabel
            control={<Switch size="small" checked={adaptiveTimeout} onChange={(event) => setAdaptiveTimeout(event.target.checked)} />}
            label="Адаптивный таймаут"
          />
          <TextField size="small" type="number" label="Ошибок до паузы" value={circuitFailures} onChange={(event) => setCircuitFailures(Number(event.target.value))} inputProps={{ min: 1, max: 10 }} />
          <TextField size="small" select label="Пауза недоступного УСПД" value={circuitCooldownSec} onChange={(event) => setCircuitCooldownSec(Number(event.target.value))} sx={{ minWidth: 210 }}>
            <MenuItem value={30}>30 секунд</MenuItem>
            <MenuItem value={60}>1 минута</MenuItem>
            <MenuItem value={120}>2 минуты</MenuItem>
            <MenuItem value={300}>5 минут</MenuItem>
            <MenuItem value={600}>10 минут</MenuItem>
          </TextField>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Разные УСПД опрашиваются параллельно. Линии одного УСПД выполняются последовательно, а недоступные УСПД временно исключаются из фонового прохода.
        </Typography>
      </Collapse>

      {suspendedGateways.length ? (
        <Alert severity="warning" variant="outlined" sx={{ mt: 1, py: 0 }}>
          Фоновый опрос временно приостановлен для УСПД: {suspendedGateways.length}. Ручная проверка остаётся доступна.
        </Alert>
      ) : null}

      {hasReport ? (
        <>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
            <Chip size="small" color={readings === total && total > 0 ? 'success' : 'warning'} label={`Получены показания: ${readings}/${total}`} />
            <Chip size="small" variant="outlined" label={`Опознано: ${Number(state?.identified) || 0}`} />
            <Chip size="small" variant="outlined" label={`Активные линии: ${Number(state?.endpoints_ok) || 0}/${Number(state?.endpoints_total) || 0}`} />
            {Number(state?.configured_endpoints_total) > Number(state?.endpoints_total) ? (
              <Chip size="small" variant="outlined" label={`Настроено портов: ${state.configured_endpoints_total}`} />
            ) : null}
            {noReply ? <Chip size="small" color="error" variant="outlined" label={`Нет ответа: ${noReply}`} /> : null}
            {mismatch ? <Chip size="small" color="error" variant="outlined" label={`Неверный номер: ${mismatch}`} /> : null}
            {Number(state?.alive_unknown) ? <Chip size="small" variant="outlined" label={`Ответ без чтения: ${state.alive_unknown}`} /> : null}
            {Number(state?.pending) ? <Chip size="small" color="info" variant="outlined" label={`Ожидают отправки: ${state.pending}`} /> : null}
          </Stack>
          {profiles.length ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {profiles.map(([profile, count]) => `${profileLabel(profile)}: ${count}`).join(' · ')}
            </Typography>
          ) : null}
          {hasProblems ? (
            <Button
              size="small"
              endIcon={<KeyboardArrowDownRoundedIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'none' }} />}
              onClick={() => setExpanded((value) => !value)}
              sx={{ mt: 0.5, px: 0 }}
            >
              {expanded ? 'Скрыть диагностику' : 'Показать диагностику'}
            </Button>
          ) : null}
          <Collapse in={expanded} unmountOnExit>
            <Stack spacing={0.75} sx={{ mt: 0.5 }}>
              {distributionWarnings.map((warning) => (
                <Alert key={warning} severity="warning" variant="outlined" sx={{ py: 0 }}>
                  {warning}
                </Alert>
              ))}
              {endpoints.map((item) => (
                <Box
                  key={item.endpoint_id || `${item.gateway_name}-${item.access}`}
                  sx={{
                    px: 1.25,
                    py: 1,
                    borderRadius: 1.75,
                    bgcolor: 'action.hover',
                    borderLeft: '3px solid',
                    borderLeftColor: collectionStatusColor(item.status),
                  }}
                >
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={0.5}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {item.gateway_name || 'УСПД'} · {item.access || 'порт'}
                    </Typography>
                    <Typography variant="caption" color={item.link_ok ? 'text.secondary' : 'error.main'}>
                      {item.link_ok
                        ? `показаний ${item.readings || 0}/${item.total || 0} · нет ответа ${item.no_reply || 0}${item.mismatch ? ` · неверный номер ${item.mismatch}` : ''}`
                        : collectionLinkMessage(item.link_message)}
                    </Typography>
                  </Stack>
                  {item.advice ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {item.advice}
                    </Typography>
                  ) : null}
                  {Array.isArray(item.reason_counts) && item.reason_counts.length ? (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                      {item.reason_counts.slice(0, 3).map((reason, index) => (
                        <Chip
                          key={`${reason.message || 'reason'}-${index}`}
                          size="small"
                          variant="outlined"
                          label={`${reason.message || 'Ошибка'}: ${reason.count || 0}`}
                          sx={{ maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                        />
                      ))}
                    </Stack>
                  ) : null}
                  {Array.isArray(item.failure_samples) && item.failure_samples.length ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                      Примеры: {item.failure_samples.map((sample) => (
                        `${sample.serial || 'без номера'}${sample.bus_address ? ` (адрес ${sample.bus_address})` : ''}`
                      )).join(', ')}
                    </Typography>
                  ) : null}
                </Box>
              ))}
            </Stack>
          </Collapse>
        </>
      ) : null}
    </Paper>
  );
}

function eventText(item) {
  const raw = String(item?.message || '').trim();
  if (raw.startsWith('agent started')) return 'Программа запущена';
  if (raw.startsWith('agent stopped')) return 'Программа остановлена';
  if (raw.startsWith('command ping')) return raw.replace(/^command ping:\s*/i, 'Связь: ');
  if (raw.startsWith('command ports')) return raw.replace(/^command ports:\s*/i, 'Порты: ');
  if (raw.startsWith('command snapshot')) return raw.replace(/^command snapshot:\s*/i, 'Состояние: ');
  if (raw.startsWith('command scan')) return raw.replace(/^command scan:\s*/i, 'Прозвон: ');
  if (raw.startsWith('command discover')) return raw.replace(/^command discover:\s*/i, 'Поиск на шине: ');
  if (raw.startsWith('command search_device')) return raw.replace(/^command search_device:\s*/i, 'Поиск прибора: ');
  if (raw.startsWith('search_device:')) return raw.replace(/^search_device:\s*/i, 'Поиск прибора: ');
  if (raw.startsWith('discover:')) return raw.replace(/^discover:\s*/i, 'Поиск: ');
  if (raw.startsWith('scan:')) return raw.replace(/^scan:\s*/i, 'Прозвон: ');
  if (raw.startsWith('подключений:')) return raw.replace('подключений:', 'Назначено подключений:');
  if (/heartbeat/i.test(raw) || /enroll failed/i.test(raw)) return 'Не удалось связаться с кабинетом';
  return raw;
}

function resultText(item) {
  const title = commandLabel(item.type);
  const ports = item.data?.serial_ports;
  const portList = Array.isArray(ports) && ports.length ? ports.join(', ') : '';
  if (item.type === 'ports' && portList) return `${title}: ${portList}`;
  if (item.type === 'ports') return `${title}: портов не видно`;
  if ((item.type === 'scan' || item.type === 'discover' || item.type === 'search_device') && item.message) {
    return `${title}: ${item.message}`;
  }
  if (item.message) return `${title}: ${item.message.replace(/^pong from\s+/i, 'ответ от ')}`;
  return item.ok ? `${title}: готово` : `${title}: не вышло`;
}

function scanStatusLabel(status) {
  switch (status) {
    case 'identified': return 'опознан';
    case 'mismatch': return 'серийник не совпал';
    case 'alive_unknown': return 'отвечает, не опознан';
    case 'link_down': return 'УСПД недоступен';
    case 'limit': return 'лимит прозвона';
    case 'no_reply': return 'нет ответа';
    default: return status || '—';
  }
}

function ScanResultDetails({ data }) {
  const endpoints = Array.isArray(data?.endpoints) ? data.endpoints : [];
  if (!endpoints.length) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 1, mb: 0.5 }}>
        План пуст — добавьте «Куда ходит» и приборы на линиях УСПД.
      </Typography>
    );
  }
  const summaryBits = [];
  if (data.identified != null) summaryBits.push(`опознано ${data.identified}`);
  if (data.mismatch) summaryBits.push(`несовп. ${data.mismatch}`);
  if (data.alive_unknown) summaryBits.push(`без id ${data.alive_unknown}`);
  if (data.no_reply) summaryBits.push(`нет ответа ${data.no_reply}`);
  return (
    <Box sx={{ ml: 1, mb: 1 }}>
      {summaryBits.length ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {summaryBits.join(' · ')}
        </Typography>
      ) : null}
      {endpoints.map((ep, index) => (
        <Box key={ep.endpoint_id || index} sx={{ mb: 0.75 }}>
          <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
            {ep.gateway_name || 'УСПД'}
            {ep.gateway_type ? ` · ${ep.gateway_type}` : ''}
            {ep.access ? ` · ${ep.access}` : ''}
            {' — '}
            {ep.link_ok ? 'порт доступен' : `порт недоступен${ep.link_message ? `: ${ep.link_message}` : ''}`}
          </Typography>
          {(ep.devices || []).map((dev, di) => {
            const expected = dev.expected_serial || dev.serial_number;
            const read = dev.read_serial;
            const status = scanStatusLabel(dev.status);
            return (
              <Typography
                key={`${dev.bus_address}-${di}`}
                variant="caption"
                color={dev.status === 'identified' ? 'text.primary' : 'text.secondary'}
                sx={{ display: 'block', pl: 1.5 }}
              >
                {expected || 'прибор'}
                {read && read !== expected ? ` → ${read}` : (read ? ` → ${read}` : '')}
                {dev.bus_address != null && dev.bus_address !== '' ? ` · адр. ${dev.bus_address}` : ''}
                {dev.line_number ? ` · лин. ${dev.line_number}` : ''}
                {dev.transport_type ? ` · ${dev.transport_type}` : ''}
                {dev.profile ? ` · ${dev.profile}` : ''}
                {' — '}
                {status}
                {dev.message && dev.status !== 'identified' ? `: ${dev.message}` : ''}
                {dev.latency_ms != null ? ` (${dev.latency_ms} мс)` : ''}
              </Typography>
            );
          })}
        </Box>
      ))}
    </Box>
  );
}

function busEndpointStatusMeta(status) {
  switch (status) {
    case 'running':
      return { label: 'идёт', color: '#0B6E4F', track: 'rgba(11,110,79,0.14)', bar: '#0B6E4F' };
    case 'done':
      return { label: 'готово', color: '#1F4B7A', track: 'rgba(31,75,122,0.12)', bar: '#1F4B7A' };
    case 'error':
      return { label: 'ошибка', color: '#9B2C2C', track: 'rgba(155,44,44,0.12)', bar: '#9B2C2C' };
    case 'cancelled':
      return { label: 'отменено', color: '#6B7280', track: 'rgba(107,114,128,0.14)', bar: '#9CA3AF' };
    case 'pending':
      return { label: 'ожидание', color: '#8A6A2F', track: 'rgba(138,106,47,0.12)', bar: '#C4A35A' };
    default:
      return { label: status || '—', color: '#6B7280', track: 'rgba(107,114,128,0.12)', bar: '#9CA3AF' };
  }
}

function groupBusJobEndpoints(endpoints) {
  const groups = [];
  const map = new Map();
  for (const ep of endpoints || []) {
    const key = ep.gateway_name || 'УСПД';
    let group = map.get(key);
    if (!group) {
      group = { gateway_name: key, gateway_type: ep.gateway_type || '', items: [] };
      map.set(key, group);
      groups.push(group);
    }
    group.items.push(ep);
  }
  return groups;
}

function BusJobProgressPanel({ job }) {
  const [openMap, setOpenMap] = useState({});
  if (!job || !Array.isArray(job.endpoints) || !job.endpoints.length) return null;
  const active = Boolean(job.active);
  const groups = groupBusJobEndpoints(job.endpoints);
  const foundTotal = job.endpoints.reduce((sum, ep) => sum + (Number(ep.found_count) || (ep.found || []).length || 0), 0);
  const doneCount = job.endpoints.filter((ep) => ep.status === 'done').length;

  return (
    <Paper
      elevation={0}
      sx={{
        ...surfaceSx,
        mb: 2,
        p: 2,
        background: active
          ? 'linear-gradient(180deg, rgba(11,110,79,0.06) 0%, var(--surface-bg) 42%)'
          : 'var(--surface-bg)',
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }} sx={{ mb: 1.5 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.2 }}>
            {job.type === 'scan_line'
              ? 'Опрос линии'
              : job.type === 'scan'
                ? 'Прозвон линий'
                : job.type === 'search_device'
                  ? 'Поиск прибора'
                  : 'Поиск на шине'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {job.summary || (active ? 'Идёт обход портов УСПД' : 'Последний прогон')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            label={active ? 'В работе' : 'Готово'}
            sx={{
              fontWeight: 600,
              bgcolor: active ? 'rgba(11,110,79,0.12)' : 'rgba(31,75,122,0.10)',
              color: active ? '#0B6E4F' : '#1F4B7A',
            }}
          />
          <Chip size="small" variant="outlined" label={`портов ${doneCount}/${job.endpoints.length}`} />
          <Chip size="small" variant="outlined" label={`найдено ${foundTotal}`} />
        </Stack>
      </Stack>

      <Stack spacing={1.75}>
        {groups.map((group) => (
          <Box key={group.gateway_name}>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.75 }}>
              {group.gateway_name}
              {group.gateway_type ? (
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1, fontWeight: 500 }}>
                  {group.gateway_type}
                </Typography>
              ) : null}
            </Typography>
            <Stack spacing={1}>
              {group.items.map((ep, index) => {
                const key = ep.endpoint_id || `${group.gateway_name}-${index}`;
                const total = Number(ep.addr_total) || 247;
                const addr = Number(ep.addr) || 0;
                const pct = ep.status === 'done' ? 100
                  : ep.status === 'pending' ? 0
                    : Math.min(100, Math.round((addr / Math.max(total, 1)) * 100));
                const found = Array.isArray(ep.found) ? ep.found : [];
                const foundCount = ep.found_count ?? found.length;
                const meta = busEndpointStatusMeta(ep.status);
                const expanded = Boolean(openMap[key]);
                return (
                  <Box
                    key={key}
                    sx={{
                      borderRadius: 2.5,
                      border: '1px solid var(--border-color)',
                      background: 'rgba(255,255,255,0.55)',
                      px: 1.5,
                      py: 1.25,
                      transition: 'border-color 160ms ease, box-shadow 160ms ease',
                      boxShadow: ep.status === 'running' ? `inset 3px 0 0 ${meta.bar}` : 'none',
                      '&:hover': { borderColor: 'rgba(0,0,0,0.16)' },
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ cursor: 'pointer', mb: 0.85 }}
                      onClick={() => setOpenMap((prev) => ({ ...prev, [key]: !prev[key] }))}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="baseline" justifyContent="space-between">
                          <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                            {ep.access || 'порт'}
                            {ep.line_label ? (
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                                лин. {ep.line_label}
                              </Typography>
                            ) : null}
                          </Typography>
                          <Typography variant="caption" sx={{ color: meta.color, fontWeight: 700, whiteSpace: 'nowrap' }}>
                            {meta.label}
                            {ep.status === 'running' || ep.status === 'done' ? ` · ${pct}%` : ''}
                          </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {job.type === 'scan_line' ? 'приборов' : 'адр.'} {addr}/{total}
                          {' · '}
                          найдено {foundCount}
                          {ep.phase === 'targeted' ? ' · поиск по secondary ID' : ''}
                        </Typography>
                      </Box>
                      <KeyboardArrowDownRoundedIcon
                        fontSize="small"
                        sx={{
                          color: 'text.secondary',
                          transform: expanded ? 'rotate(180deg)' : 'none',
                          transition: 'transform 160ms ease',
                        }}
                      />
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={pct}
                      sx={{
                        height: 7,
                        borderRadius: 99,
                        bgcolor: meta.track,
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 99,
                          bgcolor: meta.bar,
                          transition: 'transform 280ms ease',
                        },
                      }}
                    />
                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                      <Box sx={{ mt: 1.1, pt: 1, borderTop: '1px dashed var(--border-color)' }}>
                        {ep.message ? (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                            {ep.message}
                          </Typography>
                        ) : null}
                        {found.length ? (
                          <Stack spacing={0.5}>
                            {found.map((f, fi) => (
                              <Stack
                                key={`${f.bus_address}-${fi}`}
                                direction="row"
                                spacing={1}
                                justifyContent="space-between"
                                sx={{
                                  px: 1,
                                  py: 0.55,
                                  borderRadius: 1.5,
                                  bgcolor: 'rgba(0,0,0,0.03)',
                                }}
                              >
                                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                  {job.type === 'scan_line' && f.serial ? `secondary ID ${f.serial}` : (f.serial || 'без серийника')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {f.bus_address != null && f.bus_address !== '' ? `адр. ${f.bus_address}` : '—'}
                                  {f.profile ? ` · ${f.profile}` : ''}
                                </Typography>
                              </Stack>
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Пока ничего не найдено на этом порту
                          </Typography>
                        )}
                      </Box>
                    </Collapse>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
}

function DiscoverResultDetails({ data }) {
  const endpoints = Array.isArray(data?.endpoints) ? data.endpoints : [];
  if (!endpoints.length) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 1, mb: 0.5 }}>
        Нет УСПД на агенте — добавьте точки в «Куда ходит».
      </Typography>
    );
  }
  const missing = Array.isArray(data?.missing_candidates) ? data.missing_candidates : [];
  return (
    <Box sx={{ ml: 1, mb: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        найдено {data.found_total ?? 0}
        {' · '}
        совпало {data.matched_total ?? 0} из {data.candidate_count ?? 0}
        {data.missing_total ? ` · в доме не видно ${data.missing_total}` : ''}
      </Typography>
      {endpoints.map((ep, index) => (
        <Box key={ep.endpoint_id || index} sx={{ mb: 0.75 }}>
          <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
            {ep.gateway_name || 'УСПД'}
            {ep.gateway_type ? ` · ${ep.gateway_type}` : ''}
            {ep.access ? ` · ${ep.access}` : ''}
            {' — '}
            {ep.link_ok ? 'порт доступен' : `порт недоступен${ep.link_message ? `: ${ep.link_message}` : ''}`}
          </Typography>
          {(ep.matched || []).map((m, mi) => (
            <Typography key={`m-${m.device_id}-${mi}`} variant="caption" sx={{ display: 'block', pl: 1.5 }}>
              совпало: {m.expected_serial || m.read_serial}
              {m.bus_address ? ` · адр. ${m.bus_address}` : ''}
              {m.profile ? ` · ${m.profile}` : ''}
            </Typography>
          ))}
          {(ep.unmatched_found || []).map((f, fi) => (
            <Typography
              key={`u-${f.bus_address}-${fi}`}
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', pl: 1.5 }}
            >
              на шине, нет в ЛК: {f.read_serial || 'без серийника'}
              {f.bus_address ? ` · адр. ${f.bus_address}` : ''}
              {f.profile ? ` · ${f.profile}` : ''}
            </Typography>
          ))}
          {!ep.link_ok || ((ep.found || []).length === 0 && !(ep.matched || []).length) ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 1.5 }}>
              {ep.link_ok ? 'ничего не найдено на этом порту' : ''}
            </Typography>
          ) : null}
        </Box>
      ))}
      {missing.length ? (
        <Box sx={{ mt: 0.5 }}>
          <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
            В доме есть, на шине не видно
          </Typography>
          {missing.map((c, i) => (
            <Typography key={c.device_id || i} variant="caption" color="text.secondary" sx={{ display: 'block', pl: 1.5 }}>
              {c.serial_number || 'без серийника'}
              {c.name ? ` · ${c.name}` : ''}
            </Typography>
          ))}
        </Box>
      ) : null}
    </Box>
  );
}

function DeviceSearchResultDetails({ data }) {
  const endpoints = Array.isArray(data?.endpoints) ? data.endpoints : [];
  const matches = endpoints.flatMap((endpoint) => (
    (endpoint.matched || []).map((item) => ({ ...item, endpoint }))
  ));
  if (!matches.length) {
    const unavailable = endpoints.filter((item) => !item.link_ok).length;
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 1, mb: 0.5 }}>
        Совпадений нет{unavailable ? `, недоступно портов: ${unavailable}` : ''}
      </Typography>
    );
  }
  return (
    <Stack spacing={0.75} sx={{ ml: 1, mt: 0.75, mb: 1 }}>
      {matches.map(({ endpoint, ...item }, index) => (
        <Box
          key={`${endpoint.endpoint_id || index}-${item.bus_address || index}`}
          sx={{ border: '1px solid var(--border-color)', borderRadius: 2, px: 1.25, py: 0.9 }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {endpoint.gateway_name || 'УСПД'}
            {endpoint.access ? ` · ${endpoint.access}` : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {endpoint.line_label ? `Линия ${endpoint.line_label} · ` : ''}
            {item.bus_address ? `адрес ${item.bus_address} · ` : ''}
            {item.profile || 'профиль не определён'}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

function hostOnly(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (raw.startsWith('[')) {
    const end = raw.indexOf(']');
    if (end > 0) {
      const rest = raw.slice(end + 1);
      if (rest.startsWith(':') && /^\d+$/.test(rest.slice(1))) return raw.slice(0, end + 1);
    }
    return raw;
  }
  const match = raw.match(/^([^:]+):(\d+)$/);
  return match ? match[1] : raw;
}

function pulsarPortForLine(lineNumber) {
  const num = Number.parseInt(String(lineNumber ?? ''), 10);
  if (num >= 1 && num <= PULSAR_LINE_COUNT) return String(PULSAR_TCP_BASE + num - 1);
  return null;
}

function accessLabel(item) {
  if (item.kind === 'com') return item.com_port || 'COM';
  if (item.host) return `${item.host}:${item.tcp_port || ''}`;
  return 'Сеть';
}

function devicesLabel(count) {
  const n = Number(count) || 0;
  if (n === 1) return '1 прибор';
  if (n >= 2 && n <= 4) return `${n} прибора`;
  return `${n} приборов`;
}

function groupEndpointsByUspd(rows) {
  const groups = [];
  const map = new Map();
  for (const item of rows || []) {
    const key = item.gateway_id || item.id;
    let group = map.get(key);
    if (!group) {
      group = {
        gateway_id: key,
        gateway_name: item.gateway_name || 'УСПД',
        building_name: item.building_name || '',
        items: [],
      };
      map.set(key, group);
      groups.push(group);
    }
    group.items.push(item);
  }
  return groups;
}

export default function EdgeAgentsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [tokenDialog, setTokenDialog] = useState(null);
  const [copied, setCopied] = useState('');
  const [downloadBusy, setDownloadBusy] = useState('');
  const [inspect, setInspect] = useState(null);
  const [commandBusy, setCommandBusy] = useState('');
  const [deviceSearch, setDeviceSearch] = useState('');
  const [collectionBusy, setCollectionBusy] = useState(false);
  const [endpoints, setEndpoints] = useState([]);
  const [wiring, setWiring] = useState([]);
  const [endpointBusy, setEndpointBusy] = useState(false);
  const [endpointKind, setEndpointKind] = useState('tcp');
  const [endpointLayout, setEndpointLayout] = useState('pulsar_tcp8');
  const [endpointCom, setEndpointCom] = useState('');
  const [endpointHost, setEndpointHost] = useState('');
  const [endpointPort, setEndpointPort] = useState(String(PULSAR_TCP_BASE));
  const [endpointGateway, setEndpointGateway] = useState('');
  const [endpointLine, setEndpointLine] = useState('');
  const [endpointUspdName, setEndpointUspdName] = useState('');
  const [endpointBuilding, setEndpointBuilding] = useState('');
  const [buildings, setBuildings] = useState([]);

  const load = useCallback(async ({ silent } = {}) => {
    if (!silent) setLoading(true);
    if (!silent) setError('');
    try {
      setItems(await edgeAgentsApi.list());
    } catch (err) {
      setError(apiError(err, 'Не удалось загрузить список'));
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(() => load({ silent: true }), 15000);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    if (!inspect) return undefined;
    const agent = items.find((item) => item.id === inspect);
    const busyJob = Boolean(agent?.meta?.bus_job?.active)
      || (agent?.pending_commands || []).some((c) => ['discover', 'search_device', 'scan', 'scan_line', 'cancel', 'reset'].includes(c.type));
    const timer = setInterval(() => load({ silent: true }), busyJob ? 2000 : 3000);
    return () => clearInterval(timer);
  }, [inspect, items, load]);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setCreating(true);
    setError('');
    try {
      await edgeAgentsApi.create(name.trim());
      setName('');
      await load();
    } catch (err) {
      setError(apiError(err, 'Не удалось добавить'));
    } finally {
      setCreating(false);
    }
  };

  const handleToken = async (id) => {
    setError('');
    setCopied('');
    try {
      const data = await edgeAgentsApi.issueEnrollToken(id);
      setTokenDialog(data);
    } catch (err) {
      setError(apiError(err, 'Не удалось получить код'));
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm('Отключить? С компьютера на объекте больше не будет связи.')) return;
    try {
      await edgeAgentsApi.revoke(id);
      await load();
    } catch (err) {
      setError(apiError(err, 'Не удалось отключить'));
    }
  };

  const handleCopy = async (key, value) => {
    await copyText(value);
    setCopied(key);
  };

  const handleDownload = async (kind) => {
    setError('');
    setDownloadBusy(kind);
    try {
      await edgeAgentsApi.download(kind);
    } catch (err) {
      setError(apiError(err, 'Не удалось скачать программу'));
    } finally {
      setDownloadBusy('');
    }
  };

  const inspected = inspect ? items.find((item) => item.id === inspect) : null;
  const creatingUspd = endpointGateway === NEW_USPD;
  const selectedGateway = creatingUspd ? null : wiring.find((item) => item.id === endpointGateway);
  const selectedLineCount = selectedGateway?.lines?.length || 0;
  const multiLineUspd = selectedLineCount > 1;
  const pulsarLayout = endpointKind === 'tcp' && endpointLayout === 'pulsar_tcp8';
  const showSingleTcpPort = endpointKind === 'tcp' && !pulsarLayout;

  const loadBindings = useCallback(async (agentId) => {
    if (!agentId) return;
    try {
      const [rows, options, buildingRows] = await Promise.all([
        edgeAgentsApi.listEndpoints(agentId),
        edgeAgentsApi.wiringOptions(),
        buildingsApi.getBuildings(0, 500).catch(() => []),
      ]);
      setEndpoints(rows);
      setWiring(options);
      setBuildings(buildingRows || []);
    } catch (err) {
      setError(apiError(err, 'Не удалось загрузить подключения'));
    }
  }, []);

  useEffect(() => {
    if (!inspect) {
      setEndpoints([]);
      return undefined;
    }
    loadBindings(inspect);
    return undefined;
  }, [inspect, loadBindings]);

  useEffect(() => {
    if (!inspect) return undefined;
    const agent = items.find((item) => item.id === inspect);
    const firstPort = agent?.serial_ports?.[0];
    if (firstPort) setEndpointCom((current) => current || firstPort);
    return undefined;
  }, [inspect, items]);

  const handleCommand = async (id, type, data) => {
    setError('');
    setCommandBusy(type);
    try {
      const updated = await edgeAgentsApi.enqueueCommand(id, type, data);
      setItems((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      setInspect(updated.id);
    } catch (err) {
      setError(apiError(err, 'Не удалось отправить проверку'));
    } finally {
      setCommandBusy('');
    }
  };

  const handleCollectionSettings = async (payload) => {
    if (!inspected?.id) return;
    setCollectionBusy(true);
    setError('');
    try {
      const updated = await edgeAgentsApi.updateReadingCollection(inspected.id, payload);
      setItems((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
    } catch (err) {
      setError(apiError(err, 'Не удалось сохранить настройки автосбора'));
    } finally {
      setCollectionBusy(false);
    }
  };

  const handleAddEndpoint = async () => {
    if (!inspected?.id || !endpointGateway) return;
    if (creatingUspd && !endpointUspdName.trim()) return;
    if (pulsarLayout && creatingUspd && !endpointBuilding) return;
    if (pulsarLayout && !creatingUspd && !endpointHost.trim() && !hostOnly(selectedGateway?.host)) return;
    if (!pulsarLayout && endpointKind === 'tcp' && !endpointHost.trim()) return;
    if (!pulsarLayout && endpointKind === 'com' && !endpointCom.trim()) return;
    if (!pulsarLayout && !creatingUspd && multiLineUspd && endpointKind === 'tcp' && !endpointLine) return;
    setEndpointBusy(true);
    setError('');
    try {
      const host = endpointKind === 'tcp' ? hostOnly(endpointHost) || hostOnly(selectedGateway?.host) : undefined;
      let updated;
      if (creatingUspd) {
        updated = await edgeAgentsApi.createUspd(inspected.id, {
          name: endpointUspdName.trim(),
          building_id: endpointBuilding || null,
          kind: endpointKind,
          com_port: endpointKind === 'com' ? endpointCom : undefined,
          host,
          tcp_port: showSingleTcpPort ? Number(endpointPort) || 502 : undefined,
          profile: pulsarLayout ? 'pulsar_tcp8' : 'single',
        });
      } else if (pulsarLayout) {
        updated = await edgeAgentsApi.createEndpoint(inspected.id, {
          kind: 'tcp',
          host,
          gateway_id: endpointGateway,
          profile: 'pulsar_tcp8',
        });
      } else {
        updated = await edgeAgentsApi.createEndpoint(inspected.id, {
          kind: endpointKind,
          com_port: endpointKind === 'com' ? endpointCom : undefined,
          host,
          tcp_port: endpointKind === 'tcp' ? Number(endpointPort) || 502 : undefined,
          gateway_id: endpointGateway,
          line_id: endpointLine || null,
        });
      }
      setItems((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      setEndpointLine('');
      setEndpointUspdName('');
      setEndpointBuilding('');
      if (creatingUspd || pulsarLayout) {
        setEndpointGateway('');
        // Готовим форму к следующему УСПД / COM — агент многофункциональный.
        if (creatingUspd) setEndpointHost('');
      }
      await loadBindings(inspected.id);
    } catch (err) {
      setError(apiError(err, creatingUspd ? 'Не удалось создать УСПД' : 'Не удалось добавить подключение'));
    } finally {
      setEndpointBusy(false);
    }
  };

  const handleDeleteEndpoint = async (endpointId) => {
    if (!inspected?.id) return;
    setEndpointBusy(true);
    setError('');
    try {
      const updated = await edgeAgentsApi.deleteEndpoint(inspected.id, endpointId);
      setItems((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      await loadBindings(inspected.id);
    } catch (err) {
      setError(apiError(err, 'Не удалось убрать подключение'));
    } finally {
      setEndpointBusy(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            Агенты
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Один агент на ПК объекта может ходить сразу на несколько УСПД в одной LAN, на несколько COM и на несколько TCP-портов — это его основная роль.
          </Typography>
        </Box>

        <Paper sx={{ ...surfaceSx, p: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
            <TextField
              size="small"
              label="Название"
              placeholder="Котельная, офис УК"
              value={name}
              onChange={(e) => setName(e.target.value)}
              sx={{ minWidth: 260 }}
            />
            <Button variant="contained" onClick={handleCreate} disabled={creating || !name.trim()}>
              Добавить
            </Button>
            <Button startIcon={<RefreshRoundedIcon />} onClick={load} disabled={loading}>
              Обновить
            </Button>
          </Stack>
        </Paper>

        {error ? <Alert severity="error">{String(error)}</Alert> : null}

        <Paper sx={{ ...surfaceSx, overflow: 'hidden' }}>
          {loading && !items.length ? (
            <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>
          ) : !items.length ? (
            <Box sx={{ p: 4, color: 'text.secondary', textAlign: 'center' }}>
              Пока никого нет. Добавьте компьютер и подключите программу.
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Название</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell>Компьютер</TableCell>
                    <TableCell align="right">Процессор</TableCell>
                    <TableCell align="right">Память</TableCell>
                    <TableCell align="right">Диск</TableCell>
                    <TableCell>Работает</TableCell>
                    <TableCell>Порты</TableCell>
                    <TableCell>Был на связи</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((agent) => (
                    <TableRow key={agent.id} hover>
                      <TableCell>{agent.name}</TableCell>
                      <TableCell>{statusChip(agent)}</TableCell>
                      <TableCell>
                        {agent.hostname || '—'}
                        {agent.os_name ? (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {osLabel(agent.os_name)}
                          </Typography>
                        ) : null}
                        <Typography variant="caption" display="block" color="text.secondary">
                          Версия {agent.agent_version || 'не определена'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{formatPct(agent.cpu_percent)}</TableCell>
                      <TableCell align="right">{formatPct(agent.memory_percent)}</TableCell>
                      <TableCell align="right">{formatPct(agent.disk_percent)}</TableCell>
                      <TableCell>{formatUptime(agent.uptime_sec)}</TableCell>
                      <TableCell>{agent.serial_ports?.length ? agent.serial_ports.join(', ') : '—'}</TableCell>
                      <TableCell>{formatTs(agent.last_seen_at)}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          {agent.status !== 'revoked' ? (
                            <Button size="small" onClick={() => handleToken(agent.id)}>Подключить</Button>
                          ) : null}
                          {agent.status !== 'revoked' ? (
                            <Button size="small" onClick={() => setInspect(agent.id)}>Проверить</Button>
                          ) : null}
                          {agent.status !== 'revoked' ? (
                            <Button size="small" color="error" onClick={() => handleRevoke(agent.id)}>Отключить</Button>
                          ) : null}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Stack>

      <Dialog
        open={Boolean(tokenDialog)}
        onClose={() => {
          setTokenDialog(null);
          setCopied('');
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Подключение «{tokenDialog?.agent_name}»</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Код действует 30 минут и только один раз
            {tokenDialog?.expires_at ? ` — до ${formatTs(tokenDialog.expires_at)}` : ''}.
            Вставьте блок в командную строку на компьютере объекта — программа скачается и подключится сама.
          </Alert>
          {error ? <Alert severity="error" sx={{ mb: 2 }}>{String(error)}</Alert> : null}
          <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            <Button
              disabled={Boolean(downloadBusy)}
              onClick={() => handleDownload('windows')}
            >
              {downloadBusy === 'windows' ? 'Скачиваю…' : 'Скачать для Windows'}
            </Button>
            <Button
              disabled={Boolean(downloadBusy)}
              onClick={() => handleDownload('linux')}
            >
              {downloadBusy === 'linux' ? 'Скачиваю…' : 'Скачать для Linux'}
            </Button>
          </Stack>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Код</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
            <TextField
              fullWidth
              value={tokenDialog?.token || ''}
              InputProps={{ readOnly: true }}
              sx={{ fontFamily: 'monospace' }}
            />
            <Button onClick={() => handleCopy('token', tokenDialog?.token)}>
              {copied === 'token' ? 'Скопировано' : 'Копировать'}
            </Button>
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
            <Typography variant="subtitle2">Windows</Typography>
            <Button size="small" onClick={() => handleCopy('win', tokenDialog?.install_windows)}>
              {copied === 'win' ? 'Скопировано' : 'Копировать'}
            </Button>
          </Stack>
          <TextField
            fullWidth
            multiline
            value={tokenDialog?.install_windows || ''}
            InputProps={{ readOnly: true }}
            sx={{ mb: 2, fontFamily: 'monospace' }}
          />
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
            <Typography variant="subtitle2">Linux</Typography>
            <Button size="small" onClick={() => handleCopy('linux', tokenDialog?.install_linux)}>
              {copied === 'linux' ? 'Скопировано' : 'Копировать'}
            </Button>
          </Stack>
          <TextField
            fullWidth
            multiline
            value={tokenDialog?.install_linux || ''}
            InputProps={{ readOnly: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setTokenDialog(null); setCopied(''); }}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(inspected)} onClose={() => setInspect(null)} maxWidth="md" fullWidth>
        <DialogTitle>{inspected?.name}</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            {statusChip(inspected)}
            {inspected?.pending_commands?.length ? (
              <Chip size="small" color="warning" label="Ждём ответ" />
            ) : null}
            {inspected?.hostname ? (
              <Chip size="small" variant="outlined" label={inspected.hostname} />
            ) : null}
            <Chip
              size="small"
              variant="outlined"
              label={`Версия ${inspected?.agent_version || 'не определена'}`}
            />
          </Stack>
          <AgentActivity agent={inspected} />
          {!inspected?.enrolled_at ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Сначала нажмите «Подключить» и поставьте программу на компьютер.
            </Alert>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Ответ приходит в течение минуты.
            </Typography>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
            <Button
              variant="contained"
              disabled={!inspected?.enrolled_at || Boolean(commandBusy)}
              onClick={() => handleCommand(inspected.id, 'ping')}
            >
              Связь
            </Button>
            <Button
              disabled={!inspected?.enrolled_at || Boolean(commandBusy)}
              onClick={() => handleCommand(inspected.id, 'ports')}
            >
              Порты
            </Button>
            <Button
              disabled={!inspected?.enrolled_at || Boolean(commandBusy)}
              onClick={() => handleCommand(inspected.id, 'snapshot')}
            >
              Состояние
            </Button>
            <Button
              disabled={!inspected?.enrolled_at || Boolean(commandBusy)}
              onClick={() => handleCommand(inspected.id, 'scan')}
            >
              Прозвонить
            </Button>
            <Button
              disabled={!inspected?.enrolled_at || Boolean(commandBusy)}
              onClick={() => handleCommand(inspected.id, 'discover')}
            >
              Найти на шине
            </Button>
            <Button
              variant="outlined"
              color="warning"
              disabled={!inspected?.enrolled_at || Boolean(commandBusy)}
              onClick={() => handleCommand(inspected.id, 'cancel')}
            >
              Отменить
            </Button>
            <Button
              variant="outlined"
              disabled={!inspected?.enrolled_at || Boolean(commandBusy)}
              onClick={async () => {
                if (!inspected?.id) return;
                setCommandBusy('reset');
                setError('');
                try {
                  const updated = await edgeAgentsApi.resetTasks(inspected.id);
                  setItems((prev) => prev.map((row) => (row.id === updated.id ? { ...row, ...updated } : row)));
                } catch (err) {
                  setError(apiError(err, 'Не удалось сбросить задачи'));
                } finally {
                  setCommandBusy('');
                }
              }}
            >
              Сбросить задачи
            </Button>
          </Stack>
          <Paper
            variant="outlined"
            sx={{ p: 1.25, mb: 1.5, borderRadius: 2.5, bgcolor: 'var(--surface-muted)' }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              <ManageSearchRoundedIcon color="action" />
              <TextField
                size="small"
                fullWidth
                label="Серийный номер или Secondary ID"
                value={deviceSearch}
                onChange={(event) => setDeviceSearch(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && deviceSearch.trim() && !commandBusy) {
                    handleCommand(inspected.id, 'search_device', { serial_number: deviceSearch.trim() });
                  }
                }}
              />
              <Button
                variant="contained"
                startIcon={<ManageSearchRoundedIcon />}
                sx={{ whiteSpace: 'nowrap' }}
                disabled={!inspected?.enrolled_at || Boolean(commandBusy) || !deviceSearch.trim()}
                onClick={() => handleCommand(inspected.id, 'search_device', {
                  serial_number: deviceSearch.trim(),
                })}
              >
                Найти прибор
              </Button>
            </Stack>
          </Paper>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Поиск прибора проверяет все линии и УСПД этого агента. Для Modbus проверка может занять несколько минут.
            «Найти на шине» выполняет полную инвентаризацию подключений.
            Прогресс по каждому порту — ниже; отмена и сброс очереди — кнопками справа.
          </Typography>
          <BusJobProgressPanel job={inspected?.meta?.bus_job} />
          <ReadingCollectionPanel
            agent={inspected}
            busy={collectionBusy}
            commandBusy={commandBusy}
            onSave={handleCollectionSettings}
            onRun={() => handleCommand(inspected.id, 'collect_readings')}
          />
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Куда ходит</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Можно добавить несколько УСПД подряд и сколько угодно COM / IP:порт — агент многофункциональный.
          </Typography>
          {(endpoints || []).length ? (
            <Stack spacing={1.5} sx={{ mb: 2 }}>
              {groupEndpointsByUspd(endpoints).map((group) => (
                <Box key={group.gateway_id}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {group.gateway_name}
                    {group.building_name ? ` · ${group.building_name}` : ''}
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      {group.items.length} подключ.
                    </Typography>
                  </Typography>
                  <Stack spacing={0.75}>
                    {group.items.map((item) => (
                      <Box key={item.id}>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          alignItems={{ sm: 'center' }}
                          justifyContent="space-between"
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2">
                            {accessLabel(item)}
                            {item.line_number
                              ? ` → линия ${item.line_number}${item.line_name ? ` (${item.line_name})` : ''}`
                              : ' → все линии'}
                            {' · '}{devicesLabel(item.device_count)}
                          </Typography>
                          {item.reachability ? (
                            <Typography variant="caption" color="text.secondary">
                              {item.reachability}
                            </Typography>
                          ) : null}
                          </Box>
                          <Stack direction="row" spacing={0.5}>
                            {item.line_id ? (
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={endpointBusy || Boolean(commandBusy) || !item.device_count}
                                onClick={() => handleCommand(inspected.id, 'scan_line', {
                                  endpoint_id: item.id,
                                  line_id: item.line_id,
                                })}
                              >
                                Опросить линию
                              </Button>
                            ) : null}
                            <Button
                              size="small"
                              color="error"
                              disabled={endpointBusy}
                              onClick={() => handleDeleteEndpoint(item.id)}
                            >
                              Убрать
                            </Button>
                          </Stack>
                        </Stack>
                        {(() => {
                          const job = inspected?.meta?.bus_job;
                          const progress = job?.type === 'scan_line'
                            ? (job.endpoints || []).find((entry) => entry.endpoint_id === item.id)
                            : null;
                          if (!progress) return null;
                          const total = Math.max(Number(progress.addr_total) || Number(item.device_count) || 1, 1);
                          const completed = Math.min(Number(progress.addr) || 0, total);
                          const percent = Math.round((completed / total) * 100);
                          return (
                            <Box sx={{ mt: 0.75 }}>
                              <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 0.4 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Проверено {completed}/{total}
                                </Typography>
                                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                  Найдено {Number(progress.found_count) || 0}
                                </Typography>
                              </Stack>
                              <LinearProgress variant="determinate" value={percent} sx={{ height: 6, borderRadius: 99 }} />
                            </Box>
                          );
                        })()}
                      </Box>
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Пока пусто. Добавьте первый УСПД или COM — потом можно навесить ещё.
            </Typography>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Как</InputLabel>
              <Select
                label="Как"
                value={endpointKind}
                onChange={(event) => {
                  const next = event.target.value;
                  setEndpointKind(next);
                  if (next === 'com') {
                    setEndpointLayout('single');
                  } else {
                    setEndpointLayout('pulsar_tcp8');
                    setEndpointPort(String(PULSAR_TCP_BASE));
                  }
                }}
              >
                <MenuItem value="tcp">Сеть</MenuItem>
                <MenuItem value="com">COM</MenuItem>
              </Select>
            </FormControl>
            {endpointKind === 'tcp' ? (
              <FormControl size="small" sx={{ minWidth: 260 }}>
                <InputLabel>Раскладка</InputLabel>
                <Select
                  label="Раскладка"
                  value={endpointLayout}
                  onChange={(event) => {
                    const next = event.target.value;
                    setEndpointLayout(next);
                    if (next === 'single') setEndpointPort('502');
                    else setEndpointPort(String(PULSAR_TCP_BASE));
                  }}
                >
                  <MenuItem value="pulsar_tcp8">Пульсар · 8×RS485 · порты 4000–4007</MenuItem>
                  <MenuItem value="single">Один TCP-порт</MenuItem>
                </Select>
              </FormControl>
            ) : null}
            {endpointKind === 'com' ? (
              <TextField
                size="small"
                label="COM"
                placeholder="COM3"
                value={endpointCom}
                onChange={(event) => setEndpointCom(event.target.value)}
                sx={{ minWidth: 140 }}
              />
            ) : (
              <>
                <TextField
                  size="small"
                  label="IP УСПД"
                  placeholder="192.168.1.10"
                  value={endpointHost}
                  onChange={(event) => setEndpointHost(hostOnly(event.target.value))}
                  sx={{ minWidth: 180 }}
                />
                {showSingleTcpPort ? (
                  <TextField
                    size="small"
                    label="TCP-порт"
                    value={endpointPort}
                    onChange={(event) => setEndpointPort(event.target.value)}
                    sx={{ width: 110 }}
                  />
                ) : null}
              </>
            )}
            <FormControl size="small" sx={{ minWidth: 220, flex: 1 }}>
              <InputLabel>УСПД</InputLabel>
              <Select
                label="УСПД"
                value={endpointGateway}
                onChange={(event) => {
                  const next = event.target.value;
                  setEndpointGateway(next);
                  setEndpointLine('');
                  if (next === NEW_USPD) {
                    setEndpointUspdName((current) => current || inspected?.name || '');
                    if (endpointKind === 'tcp') setEndpointLayout('pulsar_tcp8');
                    return;
                  }
                  const option = wiring.find((item) => item.id === next);
                  const ip = hostOnly(option?.host);
                  if (ip) setEndpointHost(ip);
                  if ((option?.lines || []).length > 1 && endpointKind === 'tcp') {
                    setEndpointLayout('pulsar_tcp8');
                  }
                }}
              >
                <MenuItem value={NEW_USPD}>+ Новое УСПД</MenuItem>
                {wiring.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}{item.building_name ? ` · ${item.building_name}` : ''}
                    {(item.lines || []).length ? ` · ${(item.lines || []).length} лин.` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {creatingUspd ? (
              <>
                <TextField
                  size="small"
                  label="Название УСПД"
                  value={endpointUspdName}
                  onChange={(event) => setEndpointUspdName(event.target.value)}
                  sx={{ minWidth: 180 }}
                />
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Дом{pulsarLayout ? '' : ' (желательно)'}</InputLabel>
                  <Select
                    label={pulsarLayout ? 'Дом' : 'Дом (желательно)'}
                    value={endpointBuilding}
                    onChange={(event) => setEndpointBuilding(event.target.value)}
                    required={pulsarLayout}
                  >
                    {!pulsarLayout ? <MenuItem value="">Без дома</MenuItem> : null}
                    {buildings.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name || item.address || item.id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            ) : !pulsarLayout ? (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Линия RS485</InputLabel>
                <Select
                  label="Линия RS485"
                  value={endpointLine}
                  onChange={(event) => {
                    const next = event.target.value;
                    setEndpointLine(next);
                    const line = (selectedGateway?.lines || []).find((item) => item.id === next);
                    const suggested = pulsarPortForLine(line?.line_number);
                    if (suggested && endpointKind === 'tcp') setEndpointPort(suggested);
                  }}
                  disabled={!selectedGateway}
                >
                  {!multiLineUspd ? <MenuItem value="">Все линии</MenuItem> : null}
                  {(selectedGateway?.lines || []).map((line) => (
                    <MenuItem key={line.id} value={line.id}>
                      {line.line_number}{line.name ? ` · ${line.name}` : ''}
                      {pulsarPortForLine(line.line_number) ? ` · :${pulsarPortForLine(line.line_number)}` : ''}
                      {' · '}{devicesLabel(line.device_count)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}
            <Button
              variant="outlined"
              disabled={
                endpointBusy
                || !endpointGateway
                || (creatingUspd && !endpointUspdName.trim())
                || (pulsarLayout && creatingUspd && !endpointBuilding)
                || (endpointKind === 'com' && !endpointCom.trim())
                || (endpointKind === 'tcp' && !hostOnly(endpointHost) && !hostOnly(selectedGateway?.host))
                || (!pulsarLayout && !creatingUspd && multiLineUspd && endpointKind === 'tcp' && !endpointLine)
              }
              onClick={handleAddEndpoint}
            >
              {creatingUspd
                ? (pulsarLayout ? 'Создать Пульсар (8 линий)' : 'Создать и повесить')
                : (pulsarLayout ? 'Повесить 8 портов' : 'Добавить')}
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {pulsarLayout
              ? 'Пульсар: один IP на УСПД, линии RS485 1–8 = TCP 4000–4007; на шине до ~248 приборов. Потом можно добавить ещё УСПД на этого же агента.'
              : 'Один COM / один TCP-порт → одна шина. Добавляйте столько УСПД и портов, сколько нужно — агент один, точек доступа много.'}
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 0.5, mt: 1 }}>Ответы</Typography>
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, maxHeight: 280, overflow: 'auto' }}>
            {(inspected?.last_command_results || []).length ? (
              [...inspected.last_command_results].reverse().map((item) => (
                <Box key={item.id} sx={{ mb: 0.75 }}>
                  <Typography variant="body2">
                    {resultText(item)}
                  </Typography>
                  {item.type === 'scan' || item.type === 'scan_line' ? <ScanResultDetails data={item.data} /> : null}
                  {item.type === 'discover' ? <DiscoverResultDetails data={item.data} /> : null}
                  {item.type === 'search_device' ? <DeviceSearchResultDetails data={item.data} /> : null}
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">Пока пусто</Typography>
            )}
          </Paper>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Что происходило</Typography>
          <Paper variant="outlined" sx={{ p: 1.5, maxHeight: 200, overflow: 'auto' }}>
            {(inspected?.last_events || []).length ? (
              [...inspected.last_events].reverse().map((item, index) => (
                <Typography key={`${item.ts}-${index}`} variant="body2" sx={{ mb: 0.25 }}>
                  {eventText(item)}
                </Typography>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">Пока тихо</Typography>
            )}
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInspect(null)}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
